import React from 'react';
import { DocPageLayout } from '../components/DocPageLayout';
import { InfoBox } from '../components/InfoBox';
import { CodeBlock } from '../components/CodeBlock';
import { StepList } from '../components/StepList';
export function MultipleSourcesPage() {
  const steps = [{
    title: 'Create the order',
    content: <p data-id="element-1767">
          Initialize an order with the total amount. The gateway will track the
          remaining balance as each payment source is applied.
        </p>
  }, {
    title: 'Apply the first payment source',
    content: <>
          <p data-id="element-1768">
            Submit the first payment (e.g., gift card) as a partial payment
            against the order.
          </p>
          <CodeBlock language="JSON" title="First Payment - Gift Card" code={`{
  "apiOperation": "PAY",
  "order": {
    "amount": "30.00",
    "currency": "USD"
  },
  "sourceOfFunds": {
    "type": "GIFT_CARD",
    "provided": {
      "giftCard": {
        "number": "6280000000001234",
        "pin": "1234"
      }
    }
  }
}`} data-id="element-1769" />
        </>
  }, {
    title: 'Apply the remaining balance',
    content: <>
          <p data-id="element-1770">
            Submit the second payment for the remaining amount using a different
            payment source.
          </p>
          <CodeBlock language="JSON" title="Second Payment - Credit Card" code={`{
  "apiOperation": "PAY",
  "order": {
    "amount": "70.00",
    "currency": "USD"
  },
  "sourceOfFunds": {
    "type": "CARD",
    "provided": {
      "card": {
        "number": "5123450000000008",
        "securityCode": "100",
        "expiry": { "month": "05", "year": "25" }
      }
    }
  }
}`} data-id="element-1771" />
        </>
  }, {
    title: 'Verify the order status',
    content: <p data-id="element-1772">
          Retrieve the order to confirm all payments have been applied and the
          order status is CAPTURED with the full amount covered.
        </p>
  }];
  return <DocPageLayout title="Using Multiple Payment Sources" description="Combine different payment methods on a single order for split payments and flexible checkout experiences." data-id="element-1773">
      <p className="text-gray-700 mb-6" data-id="element-1774">
        The gateway supports applying multiple payment sources to a single
        order. This enables scenarios like split payments, gift card plus credit
        card combinations, and partial payments across different funding
        sources.
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1775">Use Cases</h2>
      <ul className="list-disc list-inside space-y-2 text-gray-700 mb-8 ml-4" data-id="element-1776">
        <li data-id="element-1777">
          <strong data-id="element-1778">Gift card + credit card</strong> — Apply a gift card balance
          first, then charge the remainder to a credit card
        </li>
        <li data-id="element-1779">
          <strong data-id="element-1780">Split payments</strong> — Allow customers to split a purchase
          across multiple cards
        </li>
        <li data-id="element-1781">
          <strong data-id="element-1782">Loyalty points + card</strong> — Redeem loyalty points and pay
          the balance with a card
        </li>
        <li data-id="element-1783">
          <strong data-id="element-1784">Partial payments</strong> — Accept deposits or installment
          payments over time
        </li>
      </ul>

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1785">
        Implementation Steps
      </h2>
      <StepList steps={steps} data-id="element-1786" />

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1787">
        Supported Combinations
      </h2>
      <div className="overflow-x-auto my-6" data-id="element-1788">
        <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden" data-id="element-1789">
          <thead data-id="element-1790">
            <tr className="bg-gray-50" data-id="element-1791">
              <th className="text-left px-4 py-3 font-semibold text-gray-900 border-b" data-id="element-1792">
                First Source
              </th>
              <th className="text-left px-4 py-3 font-semibold text-gray-900 border-b" data-id="element-1793">
                Second Source
              </th>
              <th className="text-left px-4 py-3 font-semibold text-gray-900 border-b" data-id="element-1794">
                Supported
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100" data-id="element-1795">
            <tr data-id="element-1796">
              <td className="px-4 py-3" data-id="element-1797">Gift Card</td>
              <td className="px-4 py-3" data-id="element-1798">Credit/Debit Card</td>
              <td className="px-4 py-3 text-green-600" data-id="element-1799">✓</td>
            </tr>
            <tr className="bg-gray-50/50" data-id="element-1800">
              <td className="px-4 py-3" data-id="element-1801">Credit Card</td>
              <td className="px-4 py-3" data-id="element-1802">Credit Card</td>
              <td className="px-4 py-3 text-green-600" data-id="element-1803">✓</td>
            </tr>
            <tr data-id="element-1804">
              <td className="px-4 py-3" data-id="element-1805">Loyalty Points</td>
              <td className="px-4 py-3" data-id="element-1806">Credit/Debit Card</td>
              <td className="px-4 py-3 text-green-600" data-id="element-1807">✓</td>
            </tr>
            <tr className="bg-gray-50/50" data-id="element-1808">
              <td className="px-4 py-3" data-id="element-1809">Digital Wallet</td>
              <td className="px-4 py-3" data-id="element-1810">Credit Card</td>
              <td className="px-4 py-3 text-gray-400" data-id="element-1811">—</td>
            </tr>
          </tbody>
        </table>
      </div>

      <InfoBox type="warning" title="Order of Operations" data-id="element-1812">
        Always apply non-card payment sources (gift cards, loyalty points)
        before card payments. If the non-card source fails or has insufficient
        funds, the customer can still complete the full payment with their card.
      </InfoBox>
    </DocPageLayout>;
}